import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, ThumbsDown, Frown, Sparkles, ArrowRight } from 'lucide-react';

const ApexWeGetIt = () => {
  const navigate = useNavigate();

  const goToApplication = () => {
    navigate('/5k-guarantee#application');
  };

  const struggles = [
    {
      id: 'struggle-1',
      icon: Phone,
      title: 'Endless Cold Calls',
      description: 'Dialling lead after lead, hoping someone picks up. It is exhausting and soul-destroying.'
    },
    {
      id: 'struggle-2',
      icon: ThumbsDown,
      title: 'Constant Rejection',
      description: 'Hearing "no" over and over chips away at your confidence. You start to dread every call.'
    },
    {
      id: 'struggle-3',
      icon: Frown,
      title: 'Feeling Sleazy',
      description: 'You got into fitness to help people, not to pressure them. Traditional sales feels wrong.'
    }
  ];

  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      {/* Background image with dark overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://customer-assets-7cd3h4nn.emergentagent.net/job_story-launch-11/artifacts/hfudgg6n_APEX%20WEB%20IMAGE.jpg')`
        }}
      />
      {/* Dark overlay with green tint */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/90 via-[#0A0A0A]/85 to-[#0d1f12]/85" />
      {/* Additional darkening on the left for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/70 to-transparent" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left side - The struggles */}
          <div>
            <span className="text-[#22C55E] text-sm font-medium tracking-[0.2em] uppercase">
              We Understand
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-6">
              Sales Can Feel Like a
              <br />
              <span className="text-[#A1A1AA]">Never-Ending Battle</span>
            </h2>
            
            <div className="space-y-6">
              {struggles.map((struggle) => (
                <div
                  key={struggle.id}
                  data-testid={struggle.id}
                  className="flex items-start gap-4 p-4 bg-[#121212] border border-[#27272A] rounded-xl"
                >
                  <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <struggle.icon className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">{struggle.title}</h3>
                    <p className="text-[#A1A1AA] text-sm">{struggle.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right side - The solution */}
          <div className="bg-gradient-to-br from-[#121212] to-[#0d1f12] border border-[#22C55E]/20 rounded-3xl p-8 md:p-10">
            <div className="w-14 h-14 rounded-xl bg-[#22C55E]/20 flex items-center justify-center mb-6">
              <Sparkles className="w-7 h-7 text-[#22C55E]" />
            </div>
            
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              But It Does Not Have to Be This Way
            </h3>
            
            <p className="text-[#A1A1AA] mb-6 leading-relaxed">
              With the <span className="text-white font-semibold">right systems, coaching, and frameworks</span>, 
              sales becomes effortless — for both you and your prospects.
            </p>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#22C55E]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#22C55E] text-xs">✓</span>
                </div>
                <span className="text-white">No more dialling hundreds of leads — focus on quality conversations</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#22C55E]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#22C55E] text-xs">✓</span>
                </div>
                <span className="text-white">No more rejection — prospects come ready to buy</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#22C55E]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#22C55E] text-xs">✓</span>
                </div>
                <span className="text-white">No more feeling sleazy — just genuine conversations that help people</span>
              </li>
            </ul>

            <button
              onClick={goToApplication}
              data-testid="we-get-it-cta"
              className="group inline-flex items-center justify-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold uppercase tracking-wide rounded-full px-8 py-4 transition-all duration-300 hover:-translate-y-0.5"
            >
              Let Us Show You How
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApexWeGetIt;
